import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapContainer, TileLayer, Polyline, CircleMarker } from "react-leaflet";
import { ArrowRight, Route, CalendarDays, BatteryCharging, BedDouble, Camera, Wand2 } from "lucide-react";
import { api, API_BASE } from "../../lib/api";
import { CARTO_DARK } from "../../lib/mapTiles";
import { useAuth } from "../../context/AuthContext";
import { trackEvent } from "../PageTracker";

export const ShowcaseTrip = () => {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/showcase").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const geom = data?.trip?.route?.geometry || [];
  const bounds = useMemo(() => {
    if (geom.length < 2) return null;
    const lats = geom.map((p) => p[0]);
    const lons = geom.map((p) => p[1]);
    return [[Math.min(...lats), Math.min(...lons)], [Math.max(...lats), Math.max(...lons)]];
  }, [geom]);

  if (!data || !bounds) return null;
  const { trip, share_token } = data;
  const days = trip.days || [];
  const chargeStops = days.flatMap((d) => d.charging_stops || []);
  const nights = days.filter((d) => d.stay).length;
  const journal = (trip.log_entries || []).filter((e) => e.photo_id);
  const stats = [
    [Route, `${Math.round(trip.route?.distance_km || 0)} km`, "totale route"],
    [CalendarDays, `${days.length} dagen`, "dag-per-dag gepland"],
    [BatteryCharging, `${chargeStops.length} laadstops`, "automatisch gepland"],
    [BedDouble, `${nights} overnachtingen`, "hotels onderweg"],
  ];

  const planYourself = () => {
    trackEvent("cta:showcase-plan");
    const prefill = {
      name: trip.name || "",
      origin_city: trip.origin_query || "",
      destination_city: trip.destination_query || "",
      round_trip: !!trip.round_trip,
      max_km_per_day: trip.max_km_per_day ? String(trip.max_km_per_day) : "",
      route_preference: trip.route_preference || "mix",
      travel_party: trip.travel_party || { adults: 2, children: 0, pets: 0 },
      accommodation_types: trip.accommodation_types?.length ? trip.accommodation_types : ["hotel"],
      vehicle: trip.vehicle || null,
      departure_time: trip.departure_time || "09:00",
      start_battery_pct: trip.start_battery_pct || 100,
    };
    sessionStorage.setItem("vr_wizard_prefill", JSON.stringify(prefill));
    navigate(user ? "/nieuwe-trip" : `/auth?next=${encodeURIComponent("/nieuwe-trip")}`);
  };

  return (
    <section id="voorbeeld" data-testid="showcase-trip-section" className="bg-[#111827] py-28 border-y border-white/10">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}>
            <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-[#74C69D] mb-4">Zie het in actie</p>
            <h2 className="font-heading font-light text-3xl sm:text-4xl tracking-tighter text-white leading-tight">
              Bekijk een échte trip:<br /><span className="text-[#74C69D]">{trip.origin?.name} → {trip.destination?.name}</span>
            </h2>
            <p className="text-white/70 mt-5 leading-relaxed max-w-md">
              Een {days.length}-daagse rondrit met de {trip.vehicle?.brand} {trip.vehicle?.model?.replace(/\s*\(\d{4}\)/, "")} — inclusief laadstops, hotels en het reisdagboek. Precies zoals jij hem zou plannen.
            </p>
            <div className="grid grid-cols-2 gap-4 mt-8 max-w-md">
              {stats.map(([Icon, value, label]) => (
                <div key={label} className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
                  <Icon className="w-4 h-4 text-[#74C69D] mb-2" />
                  <p className="font-mono font-extrabold text-lg text-white leading-none">{value}</p>
                  <p className="text-[11px] text-white/50 mt-1.5">{label}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-4 mt-9">
              <Link to={`/share/${share_token}`} data-testid="showcase-trip-cta" onClick={() => trackEvent("cta:showcase-bekijk")}
                className="inline-flex items-center gap-2 rounded-full bg-primary hover:bg-[#1B4332] text-white px-7 py-3.5 font-semibold shadow-[0_0_25px_rgba(116,198,157,0.2)] active:scale-95 transition-[transform,background-color,box-shadow] duration-200">
                Bekijk de voorbeeld-trip <ArrowRight className="w-4 h-4" />
              </Link>
              <button type="button" onClick={planYourself} data-testid="showcase-plan-yourself-btn"
                className="inline-flex items-center gap-2 rounded-full border border-white/25 hover:border-[#74C69D] text-white px-7 py-3.5 font-semibold active:scale-95 transition-[transform,border-color] duration-200">
                <Wand2 className="w-4 h-4 text-[#74C69D]" /> Plan deze trip zelf
              </button>
            </div>
            <p className="text-xs text-white/40 mt-3">Bekijken kan zonder account — zelf plannen vult de wizard alvast voor je in.</p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7, delay: 0.15 }}>
            <Link to={`/share/${share_token}`} data-testid="showcase-trip-map-link" className="block rounded-2xl overflow-hidden border border-white/10 relative group">
              <div className="h-[380px] pointer-events-none">
                <MapContainer bounds={bounds} boundsOptions={{ padding: [30, 30] }} zoomControl={false} dragging={false} scrollWheelZoom={false} doubleClickZoom={false} touchZoom={false} keyboard={false} attributionControl={false} className="h-full w-full !bg-[#1B263B]">
                  <TileLayer url={CARTO_DARK} />
                  <Polyline positions={geom} pathOptions={{ color: "#74C69D", weight: 3, opacity: 0.9 }} />
                  {chargeStops.map((s, i) => (
                    <CircleMarker key={i} center={[s.lat, s.lon]} radius={5} pathOptions={{ color: "#111827", fillColor: "#FFD166", fillOpacity: 1, weight: 1.5 }} />
                  ))}
                  <CircleMarker center={geom[0]} radius={6} pathOptions={{ color: "#fff", fillColor: "#74C69D", fillOpacity: 1, weight: 2 }} />
                </MapContainer>
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#111827]/70 via-transparent to-transparent z-[500] group-hover:from-[#111827]/40 transition-[background] duration-300" />
            </Link>
            {journal.length > 0 && (
              <div className="flex gap-3 mt-4" data-testid="showcase-journal-strip">
                {journal.slice(0, 3).map((e) => (
                  <Link key={e.id} to={`/share/${share_token}`} className="relative flex-1 rounded-xl overflow-hidden border border-white/10 aspect-[4/3] group">
                    <img src={`${API_BASE}/trips/${trip.id}/photos/${e.photo_id}?share=${share_token}`} alt={e.location_name || "Reismoment"} loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 pt-6">
                      <p className="text-[10px] text-white/85 truncate flex items-center gap-1"><Camera className="w-3 h-3 shrink-0 text-[#74C69D]" /> {e.location_name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

